import { useEffect, useState } from 'react';

/**
 * Width-driven sidebar collapse, shared by the clinician and patient layouts
 * so the breakpoint can't drift between them.
 *
 * The rail collapses to icons below `maxWidthPx`. The returned setter is a
 * manual override, which the next viewport crossing re-asserts — same
 * behaviour both layouts had when they each carried their own copy.
 */
export const useSidebarCollapsed = (maxWidthPx: number) => {
  const query = `(max-width: ${maxWidthPx}px)`;
  const [collapsed, setCollapsed] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const apply = () => setCollapsed(mql.matches);
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [query]);

  return [collapsed, setCollapsed] as const;
};

/**
 * Shared collapse breakpoint. Both layouts use it so they stay in step.
 */
export const SIDEBAR_COLLAPSE_MAX_WIDTH = 1365;
